import { http } from '../lib/api/http';
import { encodePublicationId, decodePublicationId, isNumericPublicationId } from '../utils/publicationId';
import { normalizePublicationType, DEFAULT_PUBLICATION_TYPE } from '../constants/publicationTypes';
import { normalizeAvailabilityRanges, parseAvailabilityRanges } from '../utils/availabilityRanges';
import { ensureRichText, normalizeRichTextInput } from '../utils/richText';
import { mapUnitRecord } from './unitsApi';
import { UNIT_TYPE_LABELS } from '../utils/unitMatching';
import { createNotification } from './notificationsApi';
import { uploadFileWithPresign } from './uploadsApi';
import { getSessionUser } from './sessionUser';

const COLLECTION = 'publications';
const PUBLICATION_VIEWS_COLLECTION = 'publication_views';
const DEFAULT_PUBLICATION_CURRENCY = 'MXN';
const MEMBERSHIP_PLANS_WITH_VISITOR_DETAILS = ['pro', 'premium', 'elite'];

const normalizeId = value => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  if (typeof value === 'object' && value !== null) {
    return value.id || '';
  }
  return String(value);
};

const resolveString = value => (typeof value === 'string' ? value.trim() : '');

const mapUser = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      email: '',
      username: '',
      companyName: '',
    };
  }
  const userRecord = Array.isArray(record) ? record[0] : record;
  const expandedCompany = userRecord?.expand?.company;
  const companyName =
    resolveString(userRecord?.company_name) ||
    resolveString(expandedCompany?.name) ||
    '';

  return {
    id: userRecord?.id || '',
    name: userRecord?.name || userRecord?.username || userRecord?.email || '',
    email: userRecord?.email || '',
    username: userRecord?.username || '',
    companyName,
  };
};

const mapFaqs = value => {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => ({
      question: String(item?.question ?? '').trim(),
      answer: String(item?.answer ?? '').trim(),
    }))
    .filter(item => item.question || item.answer);
};

const parseViews = record => {
  if (!record || typeof record !== 'object') return 0;
  const candidates = [
    record.views,
    record.viewCount,
    record.view_count,
    record.totalViews,
  ];
  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.round(numeric));
    }
  }
  return 0;
};

const parseFeedViews = record => {
  if (!record || typeof record !== 'object') return 0;
  const candidates = [
    record.feedViews,
    record.feedViewCount,
    record.feed_view_count,
    record.feedImpressions,
    record.feed_impressions,
  ];
  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.round(numeric));
    }
  }
  return 0;
};

const normalizeSelectValue = value => {
  if (Array.isArray(value)) {
    return value.find(Boolean) || '';
  }
  if (value === null || value === undefined) {
    return '';
  }
  return value;
};

const parsePricingField = raw => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw;
  return {};
};

const toNumberOrNull = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const coalesceNumber = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return null;
};

const ensureArray = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseDestinationPrice = raw => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.-]/g, '');
    if (!cleaned) return null;
    const numeric = Number(cleaned);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const getExpandedRecord = value => {
  if (Array.isArray(value)) {
    return value[0] || null;
  }
  return value || null;
};

const resolveCompanyRecord = record => {
  if (!record || typeof record !== 'object') {
    return null;
  }
  const expanded = getExpandedRecord(record?.expand?.company);
  if (expanded) {
    return expanded;
  }
  if (record.company && typeof record.company === 'object') {
    return getExpandedRecord(record.company);
  }
  return null;
};

const fetchSessionProfile = async () => {
  try {
    return await getSessionUser();
  } catch {
    return null;
  }
};

const buildViewerProfileUrl = (username, id) => {
  const slug = String(username || '').trim();
  if (slug) {
    return `/usuario/${encodeURIComponent(slug)}`;
  }
  const normalizedId = normalizeId(id);
  if (typeof normalizedId === 'string' && normalizedId.trim()) {
    return `/usuario/${encodeURIComponent(normalizedId.trim())}`;
  }
  return '';
};

const recordPublicationViewDetail = async ({
  publicationId,
  companyId,
  viewerId,
  viewerLabel,
  source,
}) => {
  if (!publicationId) {
    return null;
  }

  try {
    const payload = {
      publication_id: publicationId,
      company_id: companyId || null,
      viewer_id: viewerId || null,
      viewer_label: viewerLabel || (viewerId ? '' : 'Invitado'),
      source: source || 'details',
    };
    await http.post(`/${PUBLICATION_VIEWS_COLLECTION}`, payload);
    return true;
  } catch (error) {
    if (error?.status === 404) {
      console.warn(
        'El endpoint publication_views no está disponible. Configura la API para habilitar el historial de visitas a publicaciones.'
      );
      return null;
    }
    console.warn('No se pudo registrar el detalle de la visita de la publicación.', error);
    return null;
  }
};

const mapPublicationViewRecord = record => {
  if (!record) {
    return {
      id: '',
      publicationId: '',
      publicationTitle: '',
      publicationSlug: '',
      companyId: '',
      viewerId: '',
      viewerLabel: '',
      viewerUsername: '',
      viewerEmail: '',
      isGuest: true,
      created: '',
    };
  }

  const publicationRecord = getExpandedRecord(record.expand?.publication);
  const viewerRecord = getExpandedRecord(record.expand?.viewer);

  const viewerId = record.viewer || viewerRecord?.id || '';
  const viewerLabel =
    record.viewerLabel ||
    viewerRecord?.name ||
    viewerRecord?.username ||
    viewerRecord?.email ||
    (viewerId ? 'Miembro de Flotte' : 'Invitado');

  return {
    id: record.id,
    publicationId: record.publication || publicationRecord?.id || '',
    publicationTitle: publicationRecord?.title || '',
    publicationSlug: publicationRecord?.slug || '',
    companyId: record.company || publicationRecord?.company || '',
    viewerId,
    viewerLabel,
    viewerUsername: viewerRecord?.username || '',
    viewerEmail: viewerRecord?.email || '',
    isGuest: !viewerId,
    created: record.created || '',
    source: record.source || 'details',
  };
};

const normalizeDestinations = value => {
  const entries = ensureArray(value);
  return entries
    .map(item => {
      if (typeof item === 'string') {
        const label = item.trim();
        if (!label) return null;
        return {
          id: label,
          destinationCode: '',
          destinationLabel: label,
          label,
          price: null,
          priceLabel: '',
        };
      }
      if (!item || typeof item !== 'object') return null;
      const destinationCodeRaw =
        item.destinationCode ??
        item.code ??
        item.destination ??
        item.location ??
        '';
      const destinationLabelRaw =
        item.destinationLabel ??
        item.label ??
        item.destination ??
        item.location ??
        '';
      const destinationCode = String(destinationCodeRaw || '').trim();
      const destinationLabel = String(destinationLabelRaw || '').trim();
      if (!destinationCode && !destinationLabel) {
        return null;
      }
      const baseId = item?.id || item?.uid || item?.code || destinationLabel || destinationCode;
      const price =
        parseDestinationPrice(item?.price) ??
        parseDestinationPrice(item?.amount);
      const priceLabel =
        typeof item?.priceLabel === 'string' ? item.priceLabel.trim() : '';
      let entryId = baseId;
      if (!entryId) {
        if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
          entryId = crypto.randomUUID();
        } else {
          entryId = `${destinationCode || destinationLabel}-${Math.random().toString(36).slice(2, 10)}`;
        }
      }
      return {
        id: entryId,
        destinationCode,
        destinationLabel,
        label: destinationLabel || destinationCode,
        price,
        priceLabel,
      };
    })
    .filter(Boolean);
};

const formatPriceLabel = (currency, amount) => {
  const numeric = toNumberOrNull(amount);
  if (numeric === null) return '';
  const resolvedCurrency = currency || DEFAULT_PUBLICATION_CURRENCY;
  return `${resolvedCurrency} ${numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const escapeFilterValue = value => String(value || '').replace(/"/g, '\\"');

const mapRecord = record => {
  const views = parseViews(record);
  const feedViews = parseFeedViews(record);
  const companyRecord = resolveCompanyRecord(record);

  let companyHeroUrl =
    record.companyHeroUrl ||
    record.company_hero_url ||
    companyRecord?.heroUrl ||
    companyRecord?.hero_url ||
    '';

  let companyLogoUrl =
    record.companyLogoUrl ||
    record.company_logo_url ||
    companyRecord?.logoUrl ||
    companyRecord?.logo_url ||
    '';

const companyNameCandidates = [
    resolveString(record.companyName),
    resolveString(companyRecord?.name),
    resolveString(companyRecord?.nombre),
    resolveString(companyRecord?.nombre_comercial),
    resolveString(companyRecord?.nombreComercial),
    resolveString(companyRecord?.razonSocial),
    resolveString(companyRecord?.razon_social),
    resolveString(companyRecord?.businessName),
    resolveString(companyRecord?.displayName),
  ].filter(Boolean);
  const companyName = companyNameCandidates[0] || '';
  const companySlugCandidates = [
    resolveString(record.companySlug),
    resolveString(companyRecord?.slug),
    resolveString(companyRecord?.slug_empresa),
    resolveString(companyRecord?.urlSlug),
    resolveString(companyRecord?.url),
  ].filter(Boolean);
  const companySlug = companySlugCandidates[0] || '';
  const rawMembershipPlan =
    companyRecord?.membershipPlan ||
    companyRecord?.membership_plan ||
    companyRecord?.subscriptionPlan ||
    record.companyMembershipPlan ||
    record.companySubscriptionPlan ||
    record.subscriptionPlan ||
    '';
  const companyMembershipPlan = String(rawMembershipPlan || '').trim().toLowerCase() || 'free';
  const companyLocationParts = [
    companyRecord?.city,
    companyRecord?.state,
    companyRecord?.country,
  ]
    .map(value => (value || '').trim())
    .filter(Boolean);
  const companyLocationLabel = companyLocationParts.join(', ');
  const companyTaglineCandidates = [
    companyRecord?.slogan,
    companyRecord?.tagline,
    record.companyTagline,
  ]
    .map(value => (value || '').trim())
    .filter(Boolean);
  const companyTagline = companyTaglineCandidates[0] || '';
  const companyResponseTimeCandidates = [companyRecord?.sla, record.sla]
    .map(value => (value || '').trim())
    .filter(Boolean);
  const companyResponseTime = companyResponseTimeCandidates[0] || '';
  const companyCoverage = companyLocationLabel || record.location || '';
  const companyRating =
    toNumberOrNull(companyRecord?.rating) ??
    toNumberOrNull(record.companyRating) ??
    null;
  const companyProfileHref = companySlug ? `/empresa/${companySlug}` : '';
  const bannerPreference = record.bannerPreference || record.banner_preference || 'company';

  const unitRecordSource = record.unit || record?.expand?.unit;
  let unitPhotoUrl = '';
  if (unitRecordSource) {
    unitPhotoUrl = unitRecordSource.photoUrl || unitRecordSource.photo_url || '';
  }

  let bannerUrl = record.bannerUrl || record.banner_url || '';
  if (!bannerUrl) {
    if (bannerPreference === 'company' && companyHeroUrl) {
      bannerUrl = companyHeroUrl;
    } else if (bannerPreference === 'unit' && unitPhotoUrl) {
      bannerUrl = unitPhotoUrl;
    } else if (bannerPreference === 'custom') {
      bannerUrl = record.bannerUrl || record.banner_url || record.productImageUrl || record.product_image_url || '';
    }
  }
  if (!bannerUrl && unitPhotoUrl) {
    bannerUrl = unitPhotoUrl;
  }
  if (!bannerUrl && companyHeroUrl) {
    bannerUrl = companyHeroUrl;
  }

  const normalizedType = normalizePublicationType(record.type);
  let productImageUrl = record.productImageUrl || record.product_image_url || '';

  const rawUnitAvailability =
    record.unitAvailabilityRanges ??
    record.unit_availabilityRanges ??
    record.unitAvailability ??
    record.availabilityRanges ??
    record.unit_availability ??
    (record?.expand?.unitAvailabilityRanges ??
      record?.expand?.unit?.availabilityRanges);
  const unitAvailabilityRanges = parseAvailabilityRanges(rawUnitAvailability);

  let mappedUnit = null;
  if (unitRecordSource) {
    try {
      mappedUnit = mapUnitRecord(unitRecordSource);
    } catch (err) {
      console.warn('No se pudo mapear la unidad asociada a la publicacion.', err);
    }
  }
  if (!unitPhotoUrl && mappedUnit?.photoUrl) {
    unitPhotoUrl = mappedUnit.photoUrl;
  }

  const parsedPricing = parsePricingField(record.pricing);
  const parsedDestinationsFromPricing = normalizeDestinations(parsedPricing.destinations);
  const parsedDestinationsFromRecord = normalizeDestinations(record.destinationPrices);
  const resolvedDestinations =
    (mappedUnit?.destinationPrices?.length ? mappedUnit.destinationPrices : null) ??
    (parsedDestinationsFromPricing.length ? parsedDestinationsFromPricing : null) ??
    parsedDestinationsFromRecord;

  const resolvedCurrency =
    mappedUnit?.currency ||
    parsedPricing.currency ||
    record.currency ||
    DEFAULT_PUBLICATION_CURRENCY;

  const resolvedBasePrice = coalesceNumber(
    toNumberOrNull(mappedUnit?.unitPrice),
    toNumberOrNull(parsedPricing.base),
    toNumberOrNull(record.unitPrice),
    toNumberOrNull(record.price)
  );

  const customPriceLabel =
    typeof record.price === 'string' && record.price.trim() ? record.price.trim() : '';

  let resolvedPriceLabel =
    customPriceLabel ||
    record.priceLabel ||
    mappedUnit?.priceLabel ||
    (resolvedBasePrice !== null ? formatPriceLabel(resolvedCurrency, resolvedBasePrice) : '');

  if (!resolvedPriceLabel) {
    resolvedPriceLabel = 'Consulta precio';
  }

  const unitTractorType = mappedUnit?.tractorType || '';
  const unitTractorTypeLabel = unitTractorType
    ? UNIT_TYPE_LABELS[unitTractorType] || unitTractorType
    : '';

  const normalizedDestinations = Array.isArray(resolvedDestinations)
    ? resolvedDestinations.map(entry => {
        if (!entry || typeof entry !== 'object') return null;
        const priceLabel =
          entry.priceLabel && typeof entry.priceLabel === 'string' && entry.priceLabel.trim()
            ? entry.priceLabel.trim()
            : entry.price !== null && entry.price !== undefined
            ? formatPriceLabel(resolvedCurrency, entry.price)
            : '';
        return {
          ...entry,
          priceLabel,
          currency: entry.currency || resolvedCurrency,
        };
      }).filter(Boolean)
    : [];

  const pricing = {
    currency: resolvedCurrency,
    base: resolvedBasePrice,
    destinations: normalizedDestinations,
  };

  const createdByUser = mapUser(record?.expand?.createdBy);
  const updatedByUser = mapUser(record?.expand?.updatedBy);

  const primaryImageUrl =
    [
      unitPhotoUrl,
      mappedUnit?.photoUrl,
      productImageUrl,
      bannerUrl,
      companyHeroUrl,
      companyLogoUrl,
    ].find(value => typeof value === 'string' && value.trim()) || '';

  const providerName =
    sanitizeProviderName(companyName) ||
    sanitizeProviderName(createdByUser.companyName) ||
    sanitizeProviderName(record.providerName) ||
    '';

  const normalizedCompanyId = normalizeId(record.company || companyRecord?.id);
  const companyOwnerId = normalizeId(
    companyRecord?.owner ||
      companyRecord?.owner_id ||
      companyRecord?.ownerId ||
      companyRecord?.ownerID
  );

  return {
    id: record.id,
    numericId: encodePublicationId(record.id),
    title: record.title || '',
    description: ensureRichText(record.description || ''),
    type: normalizedType,
    unitName: record.unitName || '',
    unitId: record.unitId || record.unit || '',
    unitAvailabilityRanges,
    companyId: normalizedCompanyId,
    companyName,
    companySlug,
    companyLogoUrl,
    companyHeroUrl,
    companyMembershipPlan,
    companyTagline,
    companyCoverage,
    companyResponseTime,
    companyRating,
    companyProfileHref,
    companyCategory: normalizeSelectValue(companyRecord?.category),
    unitPhotoUrl,
    providerName,
    location: record.location || '',
    availableFrom: record.availableFrom || '',
    availableTo: record.availableTo || '',
    autoPauseAt: record.autoPauseAt || '',
    unitTractorType,
    unitTractorTypeLabel,
    faqs: mapFaqs(record.faqs),
    policies: record.policies || '',
    sla: record.sla || '',
    price: resolvedPriceLabel,
    priceLabel: resolvedPriceLabel,
    customPrice: customPriceLabel,
    priceCurrency: resolvedCurrency,
    pricing,
    destinationPrices: pricing.destinations,
    unitDestinationPrices: mappedUnit?.destinationPrices || [],
    unitPrice: resolvedBasePrice,
    status: record.status || 'draft',
    company: normalizedCompanyId || record.company || '',
    companyOwnerId,
    companySummary: companyRecord || null,
    createdBy: record.createdBy || createdByUser.id || '',
    createdByUser,
    updatedBy: record.updatedBy || updatedByUser.id || '',
    updatedByUser,
    unit: record.unit || '',
    unitRef: record.unitRef || '',
    unitReference: record.unitReference || '',
    bannerPreference,
    bannerUrl,
    productImageUrl,
    primaryImageUrl,
    created: record.created,
    updated: record.updated,
    views,
    viewCount: views,
    feedViews,
    feedViewCount: feedViews,
  };
};

const cleanPayload = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

const buildPayload = async payload => {
  const faqs = mapFaqs(payload.faqs);
  const availability = normalizeAvailabilityRanges(payload.unitAvailabilityRanges || []);

  const data = {
    title: payload.title?.trim() || '',
    description: normalizeRichTextInput(payload.description || ''),
    type: normalizePublicationType(payload.type) || DEFAULT_PUBLICATION_TYPE,
    unit_name: payload.unitName?.trim() || '',
    location: payload.location?.trim() || '',
    available_from: payload.availableFrom || '',
    available_to: payload.availableTo || '',
    auto_pause_at: payload.autoPauseAt || '',
    faqs,
    unit_availability: availability,
    policies: payload.policies?.trim() || '',
    sla: payload.sla?.trim() || '',
    price: payload.price?.trim() || '',
    status: payload.status || 'draft',
    company_id: payload.company || payload.companyId || '',
    unit_id: payload.unitId || '',
    banner_preference: payload.bannerPreference || 'company',
    banner_url: payload.bannerUrl || '',
  };

  if (payload.productImageFile instanceof File) {
    const presign = await uploadFileWithPresign({
      kind: 'generic',
      file: payload.productImageFile,
    });
    data.product_image_key = presign.object_key;
    data.product_image_url = presign.public_url;
  } else if (payload.productImageRemoved) {
    data.product_image_key = '';
    data.product_image_url = '';
  } else if (payload.productImageUrl) {
    data.product_image_url = payload.productImageUrl;
  }

  if (payload.companyLogoUrl) {
    data.company_logo_url = payload.companyLogoUrl;
  }
  if (payload.companyHeroUrl) {
    data.company_hero_url = payload.companyHeroUrl;
  }

  return cleanPayload(data);
};

export async function listPublications({ creatorId, companyId } = {}) {
  const params = {
    per_page: 100,
  };
  if (creatorId) {
    params.creator_id = creatorId;
  }
  if (companyId) {
    params.company_id = companyId;
  }

  const response = await http.get(`/${COLLECTION}`, { params });
  const items = Array.isArray(response.data?.items) ? response.data.items : response.data;
  return (Array.isArray(items) ? items : []).map(mapRecord);
}

export async function createPublication(payload) {
  const body = await buildPayload(payload);
  const response = await http.post(`/${COLLECTION}`, body);
  return mapRecord(response.data);
}

export async function updatePublication(id, payload) {
  if (!id) {
    throw new Error('El identificador de la publicacion es requerido.');
  }
  const body = await buildPayload(payload);
  const response = await http.patch(`/${COLLECTION}/${encodeURIComponent(id)}`, body);
  return mapRecord(response.data);
}

export async function deletePublication(id) {
  if (!id) return false;
  await http.delete(`/${COLLECTION}/${encodeURIComponent(id)}`);
  return true;
}

export async function listPublishedPublications({ limit = 24, page = 1, withMeta = false } = {}) {
  const params = {
    status: 'published',
    page,
    per_page: limit,
  };
  if (withMeta) {
    params.with_meta = true;
  }

  const response = await http.get(`/${COLLECTION}`, { params });
  const payload = response.data;
  if (withMeta && payload && Array.isArray(payload.items)) {
    const items = payload.items.map(mapRecord);
    const totalPages = Number(payload.total_pages || payload.totalPages || 0);
    const currentPage = Number(payload.page || page || 1);
    const totalItems = Number(payload.total || payload.totalItems || items.length || 0);
    const hasMore = totalPages ? currentPage < totalPages : items.length === limit;
    return {
      items,
      page: currentPage,
      totalPages,
      totalItems,
      hasMore,
    };
  }

  const items = Array.isArray(payload) ? payload : payload?.items;
  return (Array.isArray(items) ? items : []).map(mapRecord);
}

export async function listPublishedPublicationsByCompany(companyId, { limit = 12 } = {}) {
  if (!companyId) return [];

  const params = {
    status: 'published',
    company_id: companyId,
    per_page: limit,
  };

  const response = await http.get(`/${COLLECTION}`, { params });
  const items = Array.isArray(response.data?.items) ? response.data.items : response.data;
  return (Array.isArray(items) ? items : []).map(mapRecord);
}

export async function listSimilarPublications(
  { location = '', type = '', unitName = '', excludeId = '', limit = 6 } = {}
) {
  const results = [];
  const seen = new Set();
  const decodedExcludeId = excludeId && isNumericPublicationId(excludeId) ? decodePublicationId(excludeId) : excludeId;
  const baseParts = ['status="published"'];
  if (decodedExcludeId) {
    baseParts.push(`id!="${escapeFilterValue(decodedExcludeId)}"`);
  }

  const attempts = [];
  const normalizedType = type ? normalizePublicationType(type) : '';
  const hasLocation = Boolean(location?.trim());
  const hasType = Boolean(normalizedType);
  const hasUnitName = Boolean(unitName?.trim());

  if (hasLocation && hasType) {
    attempts.push([`location="${escapeFilterValue(location.trim())}"`, `type="${escapeFilterValue(normalizedType)}"`]);
  }
  if (hasLocation && hasUnitName) {
    attempts.push([`location="${escapeFilterValue(location.trim())}"`, `unitName="${escapeFilterValue(unitName.trim())}"`]);
  }
  if (hasLocation) {
    attempts.push([`location="${escapeFilterValue(location.trim())}"`]);
  }
  if (hasType) {
    attempts.push([`type="${escapeFilterValue(normalizedType)}"`]);
  }
  if (hasUnitName) {
    attempts.push([`unitName="${escapeFilterValue(unitName.trim())}"`]);
  }
  if (!attempts.length) {
    attempts.push([]);
  }

  for (const parts of attempts) {
    if (results.length >= limit) break;
    const params = {
      status: 'published',
      per_page: limit,
    };
    if (decodedExcludeId) {
      params.exclude_id = decodedExcludeId;
    }
    parts.forEach(part => {
      if (part.startsWith('location=')) {
        params.location = part.split('="')[1]?.replace(/"$/, '') || '';
      } else if (part.startsWith('type=')) {
        params.type = part.split('="')[1]?.replace(/"$/, '') || '';
      } else if (part.startsWith('unitName=')) {
        params.unit_name = part.split('="')[1]?.replace(/"$/, '') || '';
      }
    });

    try {
      const response = await http.get(`/${COLLECTION}`, { params });
      const items = Array.isArray(response.data?.items) ? response.data.items : response.data;
      (Array.isArray(items) ? items : []).forEach(item => {
        if (results.length >= limit) return;
        const mapped = mapRecord(item);
        if (!mapped || mapped.id === decodedExcludeId || seen.has(mapped.id)) {
          return;
        }
        seen.add(mapped.id);
        results.push(mapped);
      });
    } catch (error) {
      console.warn('No se pudieron obtener publicaciones similares con los parametros', params, error);
    }
  }

  return results.slice(0, limit);
}

export async function getPublicationById(id) {
  if (!id) {
    throw new Error('El identificador de la publicacion es requerido.');
  }
  const resolvedId = isNumericPublicationId(id) ? decodePublicationId(id) : id;
  if (!resolvedId) {
    throw new Error('El identificador de la publicacion no es valido.');
  }
  const response = await http.get(`/${COLLECTION}/${encodeURIComponent(resolvedId)}`);
  return mapRecord(response.data || {});
}

export async function listPublicationViewsByCompany(companyId, { limit = 50 } = {}) {
  if (!companyId) {
    return { items: [], total: 0 };
  }

  try {
    const response = await http.get(`/${PUBLICATION_VIEWS_COLLECTION}`, {
      params: {
        company_id: companyId,
        limit,
      },
    });
    const rawItems = Array.isArray(response.data) ? response.data : [];
    return {
      items: rawItems.map(mapPublicationViewRecord),
      total: rawItems.length,
    };
  } catch (error) {
    console.warn('No se pudieron obtener las visitas a publicaciones.', error);
    return { items: [], total: 0 };
  }
}

export async function getPublicationViewCount(publicationId) {
  const normalizedId = normalizeId(publicationId);
  if (!normalizedId) {
    return 0;
  }

  try {
    const response = await http.get(`/${PUBLICATION_VIEWS_COLLECTION}/count`, {
      params: { publication_id: normalizedId },
    });
    const total = Number(response.data?.total ?? 0);
    if (!Number.isFinite(total)) {
      return 0;
    }
    return Math.max(0, Math.round(total));
  } catch (error) {
    console.warn('No se pudo obtener el total de visitas de la publicación.', error);
    return 0;
  }
}

export async function registerPublicationFeedView(input) {
  const isObjectInput = input && typeof input === 'object';
  const rawIdentifier = isObjectInput ? input.id ?? input.publicationId ?? input.slug : input;
  if (!rawIdentifier) return null;

  const resolvedId = isNumericPublicationId(rawIdentifier)
    ? decodePublicationId(rawIdentifier)
    : rawIdentifier;
  if (!resolvedId) return null;

  const sessionUser = await fetchSessionProfile();
  let viewerId = (isObjectInput ? input.viewerId : '') || '';
  let viewerLabel = (isObjectInput ? input.viewerLabel : '') || '';

  if (!viewerId && sessionUser?.id) {
    viewerId = sessionUser.id;
  }
  if (!viewerLabel && sessionUser) {
    viewerLabel =
      sessionUser.displayName ||
      sessionUser.name ||
      sessionUser.email ||
      (viewerId ? 'Miembro de Flotte' : 'Invitado');
  }

  const viewerCompanyId =
    (isObjectInput ? input.viewerCompanyId : '') || sessionUser?.companyId || '';

  let publication;
  try {
    publication = await getPublicationById(resolvedId);
  } catch (error) {
    console.warn('No se pudo obtener la publicación para registrar la vista en el feed.', error);
    return null;
  }

  const companyId = normalizeId(publication.companyId);
  const ownerId = normalizeId(publication.companyOwnerId);
  const currentFeedViews = Number.isFinite(publication.feedViews) ? publication.feedViews : 0;

  if ((viewerId && ownerId && viewerId === ownerId) || (viewerCompanyId && viewerCompanyId === companyId)) {
    return currentFeedViews;
  }

  const nextValue = currentFeedViews + 1;
  let persisted = false;
  try {
    await http.patch(`/${COLLECTION}/${encodeURIComponent(resolvedId)}`, {
      feed_views: nextValue,
    });
    persisted = true;
  } catch (error) {
    console.warn('No se pudo actualizar el contador de vistas desde el feed.', error);
  }

  if (companyId) {
    recordPublicationViewDetail({
      publicationId: resolvedId,
      companyId,
      viewerId: viewerId || null,
      viewerLabel: viewerLabel || (viewerId ? 'Miembro de Flotte' : 'Invitado'),
      source: 'feed',
    }).catch(detailError => {
      console.warn('No se pudo registrar el detalle de la visita desde el feed.', detailError);
    });
  }

  return persisted ? nextValue : currentFeedViews;
}

export async function registerPublicationVisit(input) {
  const isObjectInput = input && typeof input === 'object';
  const rawIdentifier = isObjectInput ? input.id ?? input.publicationId ?? input.slug : input;
  if (!rawIdentifier) return null;

  const resolvedId = isNumericPublicationId(rawIdentifier)
    ? decodePublicationId(rawIdentifier)
    : rawIdentifier;
  if (!resolvedId) return null;

  const sessionUser = await fetchSessionProfile();
  let viewerId = (isObjectInput ? input.viewerId : '') || '';
  let viewerLabel = (isObjectInput ? input.viewerLabel : '') || '';
  let viewerUsername = (isObjectInput ? input.viewerUsername : '') || '';

  if (!viewerId && sessionUser?.id) {
    viewerId = sessionUser.id;
  }
  if (!viewerLabel && sessionUser) {
    viewerLabel =
      sessionUser.displayName ||
      sessionUser.name ||
      sessionUser.email ||
      (viewerId ? 'Miembro de Flotte' : '');
  }
  if (!viewerUsername && sessionUser?.username) {
    viewerUsername = sessionUser.username;
  }
  viewerUsername = String(viewerUsername || '').trim();

  const viewerCompanyId =
    (isObjectInput ? input.viewerCompanyId : '') || sessionUser?.companyId || '';
  const viewerDisplay = viewerLabel || (viewerId ? 'Miembro de Flotte' : 'Invitado');

  let publication;
  try {
    publication = await getPublicationById(resolvedId);
  } catch (error) {
    console.warn('No se pudo obtener la publicación para registrar la visita.', error);
    return null;
  }

  const companyId = normalizeId(publication.companyId);
  const ownerId = normalizeId(publication.companyOwnerId);

  if ((viewerId && ownerId && viewerId === ownerId) || (viewerCompanyId && viewerCompanyId === companyId)) {
    return publication;
  }

  const membershipPlanNormalized = String(publication.companyMembershipPlan || '')
    .trim()
    .toLowerCase();
  const planAllowsVisitorDetails = MEMBERSHIP_PLANS_WITH_VISITOR_DETAILS.includes(
    membershipPlanNormalized
  );

  const detailViewerId = planAllowsVisitorDetails ? viewerId || null : null;
  const detailViewerLabel = planAllowsVisitorDetails
    ? viewerDisplay
    : viewerId
    ? 'Usuario registrado'
    : 'Invitado';
  const detailViewerUsername = planAllowsVisitorDetails ? viewerUsername : '';
  const detailViewerUrl = planAllowsVisitorDetails
    ? buildViewerProfileUrl(detailViewerUsername, detailViewerId || viewerId)
    : '';

  let detailRecorded = false;
  if (companyId) {
    try {
      detailRecorded = await recordPublicationViewDetail({
        publicationId: publication.id,
        companyId,
        viewerId: detailViewerId,
        viewerLabel: detailViewerLabel,
        source: 'details',
      });
    } catch (error) {
      console.warn('No se pudo registrar el detalle de la visita de la publicación.', error);
    }
  }

  if (companyId && ownerId && (!viewerId || viewerId !== ownerId)) {
    const publicationTitle = String(publication.title || 'tu publicación');
    const detailedMessage = viewerId
      ? `${viewerDisplay} consultó la publicación "${publicationTitle}".`
      : `Un invitado consultó la publicación "${publicationTitle}".`;
    const genericMessage = `Un usuario consultó la publicación "${publicationTitle}".`;
    const message = planAllowsVisitorDetails ? detailedMessage : genericMessage;
    const data = {
      publicationId: publication.id,
      publicationTitle,
      viewerId: detailViewerId,
      viewerLabel: detailViewerLabel,
      viewerUsername: detailViewerUsername,
      viewerUrl: detailViewerUrl,
    };
    createNotification({
      type: 'publication_view',
      title: 'Nueva visita a tu publicación',
      message,
      companyId,
      destinationUrl: '/dashboard?section=publicaciones',
      createdById: detailViewerId || undefined,
      data,
    }).catch(error => {
      console.warn('No se pudo crear la notificación de visita de publicación.', error);
    });
  }

  let computedViews = null;
  try {
    computedViews = await getPublicationViewCount(resolvedId);
  } catch (error) {
    console.warn('No se pudo obtener el total de visitas de la publicación.', error);
  }

  const mappedRecord = { ...publication };
  if (typeof computedViews === 'number' && Number.isFinite(computedViews)) {
    mappedRecord.views = computedViews;
    mappedRecord.viewCount = computedViews;
  } else if (detailRecorded) {
    const fallbackValue = Number.isFinite(publication.views) ? publication.views : 0;
    mappedRecord.views = Math.max(fallbackValue, 1);
    mappedRecord.viewCount = mappedRecord.views;
  }

  return mappedRecord;
}

export async function setPublicationStatus(id, nextStatus) {
  if (!id) {
    throw new Error('El identificador de la publicacion es requerido.');
  }
  const response = await http.patch(`/${COLLECTION}/${encodeURIComponent(id)}`, {
    status: nextStatus,
  });
  return mapRecord(response.data || {});
}
  const sanitizeProviderName = value => {
    const trimmed = resolveString(value);
    if (!trimmed) return '';
    const normalized = trimmed.toLowerCase();
    if (normalized === 'proveedor' || normalized === 'provider') {
      return '';
    }
    return trimmed;
  };
